<template>
    <v-overlay v-model="loading" opacity="0.8">
        <v-progress-circular indeterminate size="64" />
        <br />
        Searching...
    </v-overlay>
    <v-container v-if="!loading" class="pa-6 d-flex align-start">
        <v-row>
            <!-- left gutter -->
            <v-col sm="0" md="1" lg="2"></v-col>
            <v-col sm="12" md="10" lg="8">
                <v-sheet
                    v-if="calcs.length > 0 || libraries.length > 0"
                    class="pa-4"
                >
                    <v-row class="text-display-medium text-typography1 mb-4">
                        <v-col
                            lg="6"
                            sm="12"
                            :class="
                                largeScreen
                                    ? 'd-flex flex-column align-start'
                                    : 'd-flex flex-column justify-center align-center'
                            "
                        >
                            <div>Search results for "{{ search }}"</div>
                            <div class="text-title-small">
                                See
                                <a
                                    href="https://learn.microsoft.com/en-us/azure/search/query-lucene-syntax#bkmk_boolean"
                                    target="__blank"
                                    >here</a
                                >
                                for how to complete more in depth searches
                            </div>
                        </v-col>
                        <v-col
                            lg="6"
                            sm="12"
                            :class="
                                largeScreen
                                    ? 'd-flex justify-end'
                                    : 'd-flex justify-center'
                            "
                        >
                            <v-btn-toggle
                                v-model="toggleFilter"
                                rounded
                                mandatory
                                density="compact"
                            >
                                <v-btn variant="outlined" color="red"
                                    >All</v-btn
                                >
                                <v-btn variant="outlined" color="red"
                                    >Libraries ({{ totalLibs }})</v-btn
                                >
                                <v-btn variant="outlined" color="red"
                                    >Calculations ({{ calcs.length
                                    }}<span v-if="calcsOverflow">+</span
                                    >)</v-btn
                                >
                            </v-btn-toggle>
                        </v-col>
                    </v-row>
                    <template v-if="showLibs">
                        <div class="pb-2 pt-4" style="font-size: 1.25rem">
                            {{
                                libraries.length > 0
                                    ? `Libraries (${totalLibs})`
                                    : showCalcs
                                    ? ``
                                    : `No libraries found for
                            "${search}".`
                            }}
                        </div>
                        <LibSearchCard
                            v-for="lib in libraries"
                            :key="lib.libraryId"
                            :library="lib"
                        />
                    </template>
                    <template v-if="showCalcs">
                        <div class="pb-2 pt-4" style="font-size: 1.25rem">
                            {{
                                calcs.length > 0
                                    ? `Calculations (${
                                          calcsOverflow ? 'Top ' : ''
                                      }${calcs.length} of ${totalCalcs})`
                                    : showLibs
                                    ? ``
                                    : `No calculations found for "${search}".`
                            }}
                        </div>
                        <CalcSearchCard
                            v-for="calc in calcs"
                            :calc="calc"
                            :key="calc.calcId"
                        ></CalcSearchCard>
                    </template>
                </v-sheet>
                <v-sheet class="d-flex justify-center" v-else>
                    <p class="text-display-medium text-typography1">
                        No search results for "{{ search }}"
                    </p>
                </v-sheet>
            </v-col>
            <!-- right gutter -->
            <v-col sm="0" md="1" lg="2"></v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import CalcSearchCard from './CalcSearchCard.vue'
import LibSearchCard from './LibSearchCard.vue'

const store = useStore()
const route = useRoute()
const { lgAndUp, xlAndUp } = useDisplay()

const loading = ref(true)
const calcs = ref([])
const totalCalcs = ref(0)
const calcsLimit = ref(50)
const libsLimit = ref(50)
const totalLibs = ref(0)
const libraries = ref([])
const toggleFilter = ref(0)

const search = computed(() => {
    if (route.params.search) return route.params.search.replaceAll('-', ' ')
    return ''
})
const showCalcs = computed(
    () => toggleFilter.value === 0 || toggleFilter.value === 2
)
const showLibs = computed(
    () => toggleFilter.value === 0 || toggleFilter.value === 1
)
const calcsOverflow = computed(() => totalCalcs.value > calcsLimit.value)
const largeScreen = computed(() => lgAndUp.value || xlAndUp.value)

onMounted(() => updatePage())

watch(route, (to, from) => {
    if (to.name === 'search' && to.params.search !== from.params.search) {
        updatePage()
    }
})

async function updatePage() {
    loading.value = true
    let { calcSearch, libSearch } = await store.dispatch('fullSearch', {
        search: search.value,
        calcsLimit: calcsLimit.value,
        libsLimit: libsLimit.value,
    })
    calcs.value = calcSearch.calcs
    totalCalcs.value = calcSearch.totalCalcs
    libraries.value = libSearch.libs
    totalLibs.value = libSearch.totalLibs
    loading.value = false
}
</script>
